import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatInr, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Mail, Check } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data?.colors?.length) setSelectedColor(r.data.colors[0]);
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) return <div className="min-h-[50vh] grid place-items-center text-sm text-[var(--fm-text-2)]">Loading…</div>;
  if (product === false) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <div className="overline">404</div>
      <h1 className="font-display text-4xl mt-3">Product not found</h1>
      <Link to="/products" className="fm-btn-primary mt-6 inline-block">Back to catalogue</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs text-[var(--fm-text-3)] mb-8">
        <Link to="/products" className="hover:text-[var(--fm-primary)]">Catalogue</Link>
        <span className="mx-2">/</span>
        <Link to={`/products?brand=${encodeURIComponent(product.brand)}`} className="hover:text-[var(--fm-primary)]">{product.brand}</Link>
        <span className="mx-2">/</span>
        <span>{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="aspect-[4/3] bg-[var(--fm-sage)] overflow-hidden">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="overline text-[var(--fm-accent)]">{product.brand} · {product.category}</div>
          <h1 className="font-display text-4xl tracking-tight mt-3 leading-tight">{product.name}</h1>
          <p className="text-[var(--fm-text-2)] mt-4 leading-relaxed">{product.short_description}</p>

          <div className="flex items-baseline gap-4 mt-7">
            <span data-testid="product-price" className="font-mono-fm text-3xl font-semibold text-[var(--fm-primary)]">{formatInr(product.price)}</span>
            {product.mrp && product.mrp > product.price && (
              <span className="font-mono-fm text-sm text-[var(--fm-text-3)] line-through">{formatInr(product.mrp)}</span>
            )}
          </div>
          <div className="text-xs text-[var(--fm-text-3)] mt-1">Exclusive of 18% GST · Free shipping on orders over ₹50,000</div>

          {product.colors?.length > 0 && (
            <div className="mt-7" data-testid="color-selector">
              <div className="overline mb-3">Colour · <span className="text-[var(--fm-text)] font-mono-fm normal-case tracking-normal">{selectedColor}</span></div>
              <div className="flex gap-3">
                {product.colors.map((c) => {
                  const sw = { Black: "#1a1a1a", Beige: "#d9c8a8", Red: "#b23a3a", Blue: "#2c4d80", Wine: "#5b1a2b" }[c] || "#888";
                  return (
                    <button
                      key={c}
                      data-testid={`color-${c.toLowerCase()}`}
                      type="button"
                      onClick={() => setSelectedColor(c)}
                      title={c}
                      className={`w-9 h-9 border-2 transition-all ${selectedColor === c ? "border-[var(--fm-primary)] scale-110" : "border-[var(--fm-border)] hover:border-[var(--fm-text-3)]"}`}
                      style={{ backgroundColor: sw }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-[var(--fm-border)]">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-[var(--fm-sage)]"><Minus size={14} strokeWidth={1.5} /></button>
              <span className="px-5 font-mono-fm text-sm" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-[var(--fm-sage)]"><Plus size={14} strokeWidth={1.5} /></button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={() => {
                const itemName = selectedColor ? `${product.name} — ${selectedColor}` : product.name;
                add({ ...product, name: itemName }, qty);
                toast.success(`${itemName} added to cart`);
              }}
              className="fm-btn-primary inline-flex items-center gap-2 flex-1 justify-center"
            >
              <ShoppingBag size={16} strokeWidth={1.5} /> Add to cart
            </button>
          </div>
          <div className="flex gap-3 mt-3">
            <button
              data-testid="buy-now-btn"
              onClick={() => {
                const itemName = selectedColor ? `${product.name} — ${selectedColor}` : product.name;
                add({ ...product, name: itemName }, qty);
                navigate("/cart");
              }}
              className="fm-btn-secondary flex-1"
            >
              Buy now
            </button>
            <button data-testid="request-quote-btn" onClick={() => setShowEnquiry(true)} className="fm-btn-accent inline-flex items-center gap-2">
              <Mail size={16} strokeWidth={1.5} /> Request quote
            </button>
          </div>
          <div className="mt-3">
            <WhatsAppButton
              className="w-full justify-center"
              label={`Chat on WhatsApp about ${product.name.split(" ").slice(0, 3).join(" ")}…`}
              message={`Hello White Molar, I'm interested in the ${product.name} (${formatInr(product.price)}). Could you share more details and current stock availability?`}
            />
          </div>

          <div className="mt-10 border-t border-[var(--fm-border)] pt-8">
            <div className="overline mb-4">Specifications</div>
            <dl className="grid grid-cols-1 gap-y-3 text-sm">
              {Object.entries(product.specs || {}).map(([k, v]) => (
                <div key={k} className="grid grid-cols-2 gap-4">
                  <dt className="text-[var(--fm-text-3)]">{k}</dt>
                  <dd className="text-[var(--fm-text)] font-mono-fm">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      <section className="mt-20 border-t border-[var(--fm-border)] pt-12">
        <div className="overline mb-4">About this product</div>
        <p className="text-[var(--fm-text-2)] leading-relaxed max-w-3xl">{product.description}</p>
      </section>

      {showEnquiry && <EnquiryDialog product={product} onClose={() => setShowEnquiry(false)} />}
    </div>
  );
}

function EnquiryDialog({ product, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", clinic_name: "", city: "", message: `I'm interested in the ${product.name}. Please share a detailed quote.` });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, product_id: product.id, product_name: product.name });
      setDone(true);
      toast.success("Enquiry submitted. We'll be in touch within 24h.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full p-8 border border-[var(--fm-border)]" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6">
            <Check size={32} className="mx-auto text-[var(--fm-primary)]" strokeWidth={1.5} />
            <h3 className="font-display text-2xl mt-4">Enquiry received</h3>
            <p className="text-sm text-[var(--fm-text-2)] mt-2">Our team will reach out within 24 business hours.</p>
            <button onClick={onClose} className="fm-btn-primary mt-6">Close</button>
          </div>
        ) : (
          <>
            <div className="overline text-[var(--fm-accent)]">Request a quote</div>
            <h3 className="font-display text-2xl mt-2">{product.name}</h3>
            <form onSubmit={submit} className="mt-5 space-y-3">
              {[
                ["name", "Full name *", "text", true],
                ["email", "Email *", "email", true],
                ["phone", "Phone *", "tel", true],
                ["clinic_name", "Clinic name", "text", false],
                ["city", "City", "text", false],
              ].map(([k, label, type, req]) => (
                <input key={k} required={req} type={type} placeholder={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-[var(--fm-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--fm-primary)]" data-testid={`enquiry-${k}`} />
              ))}
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="w-full border border-[var(--fm-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--fm-primary)]" data-testid="enquiry-message" />
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="fm-btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={busy} data-testid="enquiry-submit" className="fm-btn-accent flex-1">{busy ? "Sending…" : "Send enquiry"}</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
